import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSearchParams, updateSearchParams } from '../utils';
import { LocalStorageAcceptableKeys, LocalStorageGlobalObject } from '../types';

// TODO: currently the hook is bound to boolean valuse, update it for string | number | boolean types values
const useUrlAndStorageValue = <T extends LocalStorageAcceptableKeys>(
  urlKey: keyof LocalStorageGlobalObject[T],
  setStateFunction: React.Dispatch<React.SetStateAction<any>>,
  localStorageKey: T,
  defaultValue: any,
  value: boolean,
) => {
  const navigate = useNavigate();

  useBackButtonUrlHandler(urlKey, setStateFunction, localStorageKey, value);

  const isStringTrue = (stringVal: string): boolean => {
    return stringVal === 'true';
  };

  useEffect(() => {
    const castedUrlKey = urlKey as string;
    const urlValue = getSearchParams(castedUrlKey);

    if (urlValue) {
      setStateFunction(isStringTrue(urlValue));
      localStorage.updateObjectProperty(
        localStorageKey,
        castedUrlKey,
        isStringTrue(urlValue),
      );
    } else {
      const localStorageValue = localStorage.getObject(localStorageKey);

      if (
        localStorageValue &&
        Object.keys(localStorageValue).includes(castedUrlKey)
      ) {
        const itemValue = localStorageValue[castedUrlKey];
        if (typeof itemValue === 'boolean') {
          setStateFunction(itemValue);
        } else {
          setStateFunction(isStringTrue(itemValue));
        }
        updateSearchParams(
          castedUrlKey,
          `${localStorageValue[castedUrlKey]}`,
          true,
          navigate,
        );
      } else {
        localStorage.updateObjectProperty(
          localStorageKey,
          castedUrlKey,
          defaultValue,
        );
        updateSearchParams(castedUrlKey, `${defaultValue}`, true, navigate);
      }
    }
  }, [
    urlKey,
    setStateFunction,
    localStorageKey,
    defaultValue,
    value,
    navigate,
  ]);
};

const useBackButtonUrlHandler = (
  urlKey,
  setStateFunction,
  localStorageKey,
  stateValue,
) => {
  const location = useLocation();
  const prevLocation = useRef<string>('');

  useEffect(() => {
    if (prevLocation.current && prevLocation.current !== location.pathname) {
      const currentUrlValue = getSearchParams(urlKey as string) ?? false;

      if (currentUrlValue?.toString() !== stateValue.toString()) {
        const newValue = currentUrlValue === 'true';
        setStateFunction(newValue);
        localStorage.updateObjectProperty(
          localStorageKey,
          urlKey as string,
          currentUrlValue,
        );
      }
    }

    prevLocation.current = location.pathname;
  }, [location, stateValue]);
};

export default useUrlAndStorageValue;
